import React from "react";
import AppHeading from "../layout/parts/app-heading";
import AppBody from "../layout/parts/app-body";
import Card from "../components/card";
import SimpleInfoBlock from "../components/graphs/simple-info-block";
import LineChart from "../components/graphs/line-chart";
import ProgressBarList from "../components/graphs/progress-bar-list";
import axios, { responseHandler, errorHandler } from "../requests/axios.js";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ScaleRoundedIcon from "@mui/icons-material/ScaleRounded";
import CasinoRoundedIcon from "@mui/icons-material/CasinoRounded";
import WeekendRoundedIcon from "@mui/icons-material/WeekendRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { subDays } from "date-fns";
import { toast } from "react-toastify";
import { useAuth } from "../providers/auth-provider.jsx";
import ChannelSelector from "../components/channel-selector.jsx";
import DateSelector from "../components/date-selector.jsx";
import dateToYmd from "../functions/date-to-ymd.js";
import Loader from "../common/loader.jsx";
import GstvAgGrid from "../components/gstv-ag-grid.jsx";
import toTitleCase from "../functions/to-title-case.js";

const FREEVIEW_CHANNEL_OPTIONS = [
  { label: "MBC", external_id: "MBC" },
  { label: "Trailblazer", external_id: "Trailblazer" },
  { label: "Odyssey", external_id: "Odyssey" },
];

export default function AvodPage() {
  const NO_AVOD_CHANNELS_TEXT = `It looks like you don't have any registered AVOD channels, if you feel like this is a mistake please reach out to us.`;
  const [chartData, setChartData] = React.useState({});
  const [dateRange, setDateRange] = React.useState({
    start: subDays(new Date(), 30),
    end: new Date(),
  });
  const [hasAvodAccess, setHasAvodAccess] = React.useState(false);
  const [selectedChannels, setSelectedChannels] = React.useState([]);
  const [channelsLoading, setChannelsLoading] = React.useState(true);
  const { user } = useAuth();

  const [programReport, setProgramReport] = React.useState([]);
  const [programReportLoading, setProgramReportLoading] = React.useState(true);

  const updateSelectedChannels = React.useCallback((channelOptions) => {
    setSelectedChannels(channelOptions);
  }, []);

  // {start, end}
  const updateDateRange = React.useCallback((rangeObject) => {
    setDateRange(rangeObject);
  }, []);

  React.useEffect(() => {
    axios
      .get(
        `api/organisations/${user.organisation.id}/channels?channel_type=avod`
      )
      .then(
        responseHandler((response) => {
          setHasAvodAccess(response.data.length > 0);
        })
      )
      .catch(errorHandler((e) => {}))
      .finally(() => setChannelsLoading(false));
  }, [user.organisation.id]);

  React.useEffect(() => {
    const controller = new AbortController();

    if (hasAvodAccess) {
      const payload = {
        start: dateToYmd(dateRange.start),
        end: dateToYmd(dateRange.end),
      };

      if (selectedChannels.length) {
        payload.channels = selectedChannels
          .map((channel) => `&channels[]=${channel.value}`)
          .join("");
      }

      axios
        .get(
          `api/pages/avod?start_date=${payload.start}&end_date=${payload.end}${
            payload.channels ?? ""
          }`,
          { signal: controller.signal }
        )
        .then(
          responseHandler((response) => {
            setChartData(response.data);
          })
        )
        .catch(
          errorHandler((e) => {
            if (e.name && e.name === "CanceledError") {
              // do nothing, cancelling old request
            } else {
              console.error(e);
              toast.error("Error loading AVOD reporting data");
            }
          })
        );
    }

    return () => {
      if (hasAvodAccess) {
        controller.abort();
      }
    };
  }, [hasAvodAccess, dateRange.end, dateRange.start, selectedChannels]);

  // table data
  React.useEffect(() => {
    const controller = new AbortController();

    if (hasAvodAccess) {
      setProgramReportLoading(true);
      const payload = {
        start: dateToYmd(dateRange.start),
        end: dateToYmd(dateRange.end),
      };

      axios
        .get(
          `api/pages/avod/programs?channel_type=avod&start_date=${
            payload.start
          }&end_date=${payload.end}`,
          { signal: controller.signal }
        )
        .then(
          responseHandler((response) => {
            setProgramReport(response.data);
            setColDefs(
              Object.keys(response.data[0]).map((entry) => ({
                field: entry,
                headerName: toTitleCase(entry),
                width: 180,
              }))
            );
          })
        )
        .catch(
          errorHandler((e) => {
            if (e.name && e.name === "CanceledError") {
              // do nothing, cancelling old request
            } else {
              console.error(e);
              toast.error("Error loading AVOD program data");
            }
          })
        )
        .finally(() => setProgramReportLoading(false));
    }
    return () => {
      if (hasAvodAccess) {
        controller.abort();
      }
    };
  }, [hasAvodAccess, dateRange.end, dateRange.start]);

  // ag grid
  const defaultColDef = React.useMemo(() => {
    return {
      filter: true,
      editable: false,
    };
  }, []);
  const [colDefs, setColDefs] = React.useState();

  return (
    <React.Fragment>
      <AppHeading />

      <AppBody loading={channelsLoading}>
        <div className="avod-page">
          {!channelsLoading && !hasAvodAccess ? (
            <div className="avod-page__no-channels">
              {NO_AVOD_CHANNELS_TEXT}
            </div>
          ) : (
            <React.Fragment>
              <div className="avod-page__header-row">
                <ChannelSelector
                  channels={FREEVIEW_CHANNEL_OPTIONS}
                  onChange={updateSelectedChannels}
                />
                <DateSelector updateRange={updateDateRange} />
              </div>
              <div className="avod-page__header-row">
                <SimpleInfoBlock
                  label="Unique Viewers"
                  data={chartData.content_unique_viewers}
                  icon={<PersonRoundedIcon />}
                />
                <SimpleInfoBlock
                  label="Total Sessions"
                  data={chartData.sessions}
                  icon={<WeekendRoundedIcon />}
                />
                <SimpleInfoBlock
                  label="Total Impressions"
                  data={chartData.impressions}
                  icon={<VisibilityRoundedIcon />}
                />
                <SimpleInfoBlock
                  label="Opportunities"
                  data={chartData.opportunities}
                  icon={<CasinoRoundedIcon />}
                />
                <SimpleInfoBlock
                  label="Total Revenue"
                  data={chartData.revenue}
                  icon={<PaidRoundedIcon />}
                />
                <SimpleInfoBlock
                  label="CPM"
                  data={chartData.cpm}
                  icon={<ScaleRoundedIcon />}
                />
              </div>
              <div className="avod-page__body">
                <div className="avod-page__left">
                  <Card height={400}>
                    <LineChart
                      title="Minutes Watched"
                      subtitle=""
                      datasets={chartData.minutes_watched?.datasets || []}
                    />
                  </Card>
                  <Card height={400}>
                    <LineChart
                      title="Revenue and Cost"
                      subtitle=""
                      datasets={chartData.revenue_and_cost?.datasets || []}
                    />
                  </Card>
                </div>
                <div className="avod-page__right">
                  <Card>
                    <ProgressBarList
                      title="Top Programs"
                      data={chartData.top_programs_by_minutes?.datasets}
                    />
                  </Card>
                  <Card>
                    <ProgressBarList
                      title="Top Programs (Unique Viewers)"
                      data={chartData.top_programs_by_viewers?.datasets}
                    />
                  </Card>
                  <Card>
                    <ProgressBarList
                      title="Top Channels"
                      data={chartData.top_channel_platforms?.datasets}
                    />
                  </Card>
                </div>
              </div>

              <div className="avod-page__lower">
                {programReportLoading ? (
                  <div>
                    <Loader />
                  </div>
                ) : (
                  <GstvAgGrid
                    data={programReport}
                    defs={colDefs}
                    defaultDef={defaultColDef}
                  />
                )}
              </div>
            </React.Fragment>
          )}
        </div>
      </AppBody>
    </React.Fragment>
  );
}
